import { useTranslation } from "react-i18next";
import type { ActionContext, ActionSource } from "~/actions/types";
import { useApi } from "~/api/provider";
import { useStarActions, useViewer } from "~/api/queries";
import { useNavigateTo } from "~/foundation/components/navigation";
import { useAbilitiesLookup } from "~/policies/usePolicy";
import { useShellStore } from "~/shell/store";

export interface ActionContextOverrides {
  source?: ActionSource;
  activeEntityId?: string;
}

/**
 * Assembles everything an action is allowed to touch. Actions never import stores, the API,
 * or the router directly — they receive them, which is what makes them testable in isolation.
 */
export function useActionContext(overrides: ActionContextOverrides = {}): ActionContext {
  const { t } = useTranslation();
  const navigate = useNavigateTo();
  const api = useApi();
  const abilities = useAbilitiesLookup();
  const { data: viewer } = useViewer();
  const stars = useStarActions();
  const sceneEntityId = useShellStore((state) => state.activeEntityId);

  return {
    t: (key, options) => t(key, options ?? {}) as string,
    navigate,
    api,
    viewer,
    abilities,
    // W10 §2 — the star lookup + toggle. `useStarActions` reads the cache WITHOUT subscribing:
    // this context is built by every row and every menu, and a subscription each would be real
    // render cost for a question only a menu asks.
    stars,
    shell: useShellStore,
    source: overrides.source ?? "button",
    activeEntityId: overrides.activeEntityId ?? sceneEntityId ?? undefined,
  };
}

/** Non-hook form for tests and for callers that already hold the pieces. */
export function createActionContext(
  parts: Omit<ActionContext, "source"> & { source?: ActionSource },
): ActionContext {
  return { ...parts, source: parts.source ?? "button" };
}

export function withSource(context: ActionContext, source: ActionSource): ActionContext {
  return context.source === source ? context : { ...context, source };
}
